import { RowData } from '@tanstack/react-table';
import { forwardRef } from 'react';
import type { CSSProperties } from 'react';
import DataTableInner from './organisms/DataTableInner';
import type { IDataTableRef, IDataTableQueryFnParams, IDataTableProps, IDataTableUtilityFunctions } from './shared/types';

export { default as DataTableSelectCheckbox } from './atoms/DataTableSelectCheckbox';
export type { IDataTableSelectCheckboxProps } from './atoms/DataTableSelectCheckbox';
export { createDataTableSelectColumn } from './shared/createDataTableSelectColumn';
export type { ICreateDataTableSelectColumnOptions } from './shared/createDataTableSelectColumn';

/**
 * TanStack Table kütüphanesine DataTable'a özel meta veri alanları ekler.
 * Bu genişletmeler sayesinde kolon ve hücre seviyesinde özel stil ve sınıf tanımlamaları yapılabilir.
 */
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /**
     * Kolon başlığına uygulanacak CSS sınıfları.
     * Kolon tanımında meta.columnClassName olarak kullanılır.
     * Örnek: 'text-center fw-bold text-primary'
     */
    columnClassName?: string;
    /**
     * Kolon başlığına uygulanacak inline CSS stilleri.
     * Kolon tanımında meta.columnStyle olarak kullanılır.
     * Örnek: { backgroundColor: '#f0f0f0', minWidth: '150px' }
     */
    columnStyle?: CSSProperties;

    /**
     * Bu kolondaki tüm hücrelere uygulanacak CSS sınıfları.
     * Kolon tanımında meta.cellClassName olarak kullanılır.
     * Örnek: 'text-muted fs-7'
     */
    cellClassName?: string;
    /**
     * Bu kolondaki tüm hücrelere uygulanacak inline CSS stilleri.
     * Kolon tanımında meta.cellStyle olarak kullanılır.
     * Örnek: { padding: '8px', textAlign: 'right' }
     */
    cellStyle?: CSSProperties;
  }

  /**
   * Tablo meta verilerine DataTable utility fonksiyonlarını ekler.
   * Bu fonksiyonlar table.options.meta üzerinden erişilebilir ve
   * satır silme, geçici veri saklama gibi işlemler için kullanılır.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface TableMeta<TData extends RowData> extends IDataTableUtilityFunctions {}
}

/**
 * DataTable - Sunucu Tarafı Veri Yönetimli Tablo Componenti
 *
 * Bu component, sunucu tarafı pagination, filtreleme ve veri yönetimi özelliklerine sahip
 * gelişmiş bir tablo çözümüdür. React Query ile entegre çalışır ve TanStack Table
 * kütüphanesini kullanarak güçlü bir tablo deneyimi sunar.
 *
 * Özellikler:
 * - Sunucu tarafı pagination (sayfalama)
 * - Dinamik filtreleme
 * - Otomatik veri önbellekleme (5 dakika)
 * - Loading, error ve empty state yönetimi
 * - Satır seviyesinde geçici veri saklama
 * - Kolon ve hücre seviyesinde stil özelleştirme
 * - Ref üzerinden programatik kontrol
 *
 * @template TData - Tabloda gösterilecek veri tipi. Object tipinde olmalıdır.
 *                   Örnek: { id: number, name: string, email: string }
 *
 * @template TQueryFn - Veri çekme fonksiyonunun tipi. IDataTableQueryFnParams alıp Promise döndürmelidir.
 *
 * @example
 * ```tsx
 * // Kolon tanımları
 * const columns: ColumnDef<User>[] = [
 *   {
 *     accessorKey: 'name',
 *     header: 'İsim',
 *     meta: {
 *       columnClassName: 'text-center',
 *       cellClassName: 'fw-bold'
 *     }
 *   },
 *   {
 *     accessorKey: 'email',
 *     header: 'E-posta'
 *   }
 * ];
 *
 * // API'den veri çekme fonksiyonu
 * const fetchUsers = async (params: IDataTableQueryFnParams) => {
 *   const response = await axios.post('/api/users', {
 *     vendorId: params.vendorId,
 *     filters: params.filters,
 *     page: params.pagination.pageIndex + 1,
 *     limit: params.pagination.pageSize
 *   });
 *   return response.data;
 * };
 *
 * // API yanıtını normalize etme
 * const adaptResponse = (response: any): IDataTableNormalizedResponse<User> => {
 *   return {
 *     items: response.data,
 *     totalCount: response.total,
 *     itemCount: response.data.length,
 *     pageCount: Math.ceil(response.total / response.limit),
 *     currentPage: response.page,
 *     limit: response.limit
 *   };
 * };
 *
 * // Component kullanımı
 * const tableRef = useRef<IDataTableRef>(null);
 *
 * <DataTable
 *   ref={tableRef}
 *   columns={columns}
 *   queryKey={['users']}
 *   queryFn={fetchUsers}
 *   responseAdapter={adaptResponse}
 *   initials={{
 *     filters: { status: 'active' },
 *     pagination: { pageIndex: 0, pageSize: 20 }
 *   }}
 *   classNames={{
 *     wrapper: 'custom-wrapper',
 *     table: 'custom-table'
 *   }}
 * />
 *
 * // Ref üzerinden programatik kontrol
 * tableRef.current?.setFilters({ status: 'inactive' });
 * tableRef.current?.nextPage();
 * ```
 *
 * @param props.columns - TanStack Table ColumnDef formatında kolon tanımları.
 *                        Her kolon için header, accessorKey, cell renderer vb. tanımlanabilir.
 *
 * @param props.queryKey - React Query için cache key. String array veya fonksiyon olabilir.
 *                         Fonksiyon olarak verilirse, params'a göre dinamik key oluşturulur.
 *                         Örnek: ['users'] veya (params) => ['users', params.filters.status]
 *
 * @param props.queryFn - API'den veri çeken async fonksiyon. IDataTableQueryFnParams alır ve
 *                        Promise döndürür. Bu fonksiyon her filtre/pagination değişikliğinde
 *                        otomatik olarak çağrılır.
 *
 * @param props.responseAdapter - API yanıtını IDataTableNormalizedResponse formatına çeviren fonksiyon.
 *                                Bu fonksiyon olmadan tablo veriyi doğru şekilde gösteremez.
 *                                Mutlaka items, totalCount, itemCount, pageCount, currentPage, limit
 *                                alanlarını içeren bir obje döndürmelidir.
 *
 * @param props.initials - Component ilk render edildiğinde kullanılacak başlangıç değerleri.
 *                        - filters: Başlangıç filtreleri. Örnek: { status: 'active', category: 'electronics' }
 *                        - pagination: Başlangıç sayfa ve limit değerleri. Varsayılan: { pageIndex: 1, pageSize: 10 }
 *
 * @param props.classNames - Tablonun farklı bölümlerine özel CSS sınıfları eklemek için kullanılır.
 *                          - wrapper: Tablo wrapper div'i
 *                          - table: <table> elementi
 *                          - tableHeader: <thead> elementi
 *                          - tableHeaderRow: <tr> elementi (header içinde)
 *                          - tableBody: <tbody> elementi
 *                          - tableBodyRow: <tr> elementi (body içinde)
 *                          - footer: Footer wrapper div'i
 *                          - paginationWrapper: Pagination wrapper div'i
 *                          - pageSizeWrapper: Page size select wrapper div'i
 *
 * @param props.paginationProps - Pagination component'ine özel props. ReactPaginate props'larını
 *                               genişletir. previousLabel, nextLabel için özel sınıflar tanımlanabilir.
 *
 * @param props.pageSizeProps - Page size select box için özel props.
 *                             - options: Gösterilecek sayfa boyutu seçenekleri. Varsayılan: [10, 20, 50, 100]
 *                             - wrapperClassName: Select wrapper için CSS sınıfı
 *                             - selectClassName: Select elementi için CSS sınıfı
 *
 * @param ref - Component referansı. useRef<IDataTableRef>() ile oluşturulur.
 *             Ref üzerinden filtreleri güncelleyebilir, pagination kontrolü yapabilirsiniz.
 *             - queryKey: Mevcut query key'i
 *             - filters: Mevcut filtreler
 *             - setFilters: Filtreleri güncelleme fonksiyonu
 *             - resetFilters: Filtreleri sıfırlama fonksiyonu
 *             - pagination: Mevcut pagination durumu
 *             - setPagination: Pagination'ı güncelleme fonksiyonu
 *             - nextPage: Bir sonraki sayfaya geçme
 *             - previousPage: Bir önceki sayfaya geçme
 *             - resetPagination: Pagination'ı sıfırlama
 *             - setLimit: Sayfa başına kayıt sayısını ayarlama
 *
 * @returns React.ReactElement | null - Tablo component'i veya null (eğer queryKey boşsa)
 */
export const DataTable = forwardRef(DataTableInner) as <TData extends object, TQueryFn extends (params: IDataTableQueryFnParams) => Promise<any>>(
  props: IDataTableProps<TData, TQueryFn> & { ref?: React.Ref<IDataTableRef> },
) => React.ReactElement | null;

export default DataTable;
